import { useCallback, useMemo } from "react";
import { useAuthStore } from "@/store/auth/auth.store";
import { useRolesStore } from "@/store/roles/roles.store";
import { getUserPermissionKeys, MODULES_WITHOUT_ACCESS, TPermissionKey } from "@/utils/permissions";

const useAuthUser = () => {
  const user = useAuthStore((s) => s.user);
  const rolesCatalog = useRolesStore((s) => s.roles);

  const userPermissions = useMemo(
    () => getUserPermissionKeys(user, rolesCatalog),
    [user, rolesCatalog]
  );

  const hasPermission = useCallback(
    (required: TPermissionKey | TPermissionKey[]) => {
      // Legacy admins bypass every permission check.
      if (user.isAdmin) return true;
      const requiredPermissions = Array.isArray(required) ? required : [required];
      // Every non-access permission ("module:edit", "module:delete", …) implicitly
      // requires the module's "access" permission. Callers only pass the specific
      // action and the gate check lives here — no more `x:access && x:edit` at call sites.
      return requiredPermissions.some((k) => {
        if (!userPermissions.has(k)) return false;
        const sep = k.indexOf(":");
        if (sep < 0) return true;
        const action = k.slice(sep + 1);
        if (action === "access") return true;
        const mod = k.slice(0, sep);
        if (MODULES_WITHOUT_ACCESS.has(mod)) return true;
        return userPermissions.has(`${mod}:access` as TPermissionKey);
      });
    },
    [user.isAdmin, userPermissions]
  );

  return { user, hasPermission };
};

export default useAuthUser;
